/**
 * useCanvasStage — owns the ViewportTransform, the Canvas2D render
 * loop (edges only; cards are DOM), the stage→canvas event forwarding,
 * and the zoom helpers.
 *
 * Mount-once contract: the viewport and render loop are created once
 * per canvas mount. Frame-varying data (edges) reaches the loop via a
 * ref — the previous implementation listed `edges` in the effect deps,
 * so every edge refetch tore down and re-created the ViewportTransform
 * and silently reset the user's pan/zoom.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ViewportTransform } from '@arkturian/canvas-engine'
import type {
  LayoutEngine,
  EdgeRenderer,
  EdgeStatePool,
  TextBlock,
  BlockEdge,
} from '@arkturian/canvas-engine'
import type { CardRect } from './layouts'

export interface CanvasStage {
  viewport: ViewportTransform | null
  stageSize: { w: number; h: number }
  /** Screen (clientX/Y) → world coordinates via the inverse transform. */
  screenToWorld: (clientX: number, clientY: number) => { x: number; y: number }
  /** Fit all given rects into the viewport with breathing room. */
  zoomToOverview: (rects: CardRect[]) => void
  /** Reset to scale=1, origin — cards at readable size. */
  zoomTo100: () => void
}

export function useCanvasStage(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  stageRef: React.RefObject<HTMLDivElement | null>,
  engine: LayoutEngine<TextBlock>,
  edgeRenderer: EdgeRenderer,
  edgeStatePool: EdgeStatePool,
  edges: BlockEdge[],
): CanvasStage {
  const [viewport, setViewport] = useState<ViewportTransform | null>(null)

  // Frame-varying data crosses into the rAF loop via a ref, committed
  // from an effect (not during render) per the react-hooks ref rule.
  const edgesRef = useRef<BlockEdge[]>([])
  useEffect(() => {
    edgesRef.current = edges
  }, [edges])

  // Live stage size — drives the timeline X-span so the axis spans the
  // visible canvas width at scale=1.0.
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const measure = () =>
      setStageSize({ w: stage.clientWidth, h: stage.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(stage)
    return () => ro.disconnect()
  }, [stageRef])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const vp = new ViewportTransform(canvas)
    // Whiteboard semantics: classic Miro/Figma left-click pan, no zoom
    // limits (user must be able to zoom out to see everything), no
    // translation rubber-band (free infinite pan beyond the bbox).
    vp.setEnableLeftClickPan(true)
    vp.setEnableRubberBandingScale(false)
    vp.setEnableRubberBandingTranslation(false)
    setViewport(vp)

    // Own render loop (canvas-engine's CanvasRenderer is Product-
    // domain-specific). Edges only — cards live in the DOM overlay;
    // painting card frames as pixels caused a ghost-during-drag (DOM
    // snaps, InterpolatedProperty lags 0.4s).
    let rafId = 0
    const render = () => {
      // update() tweens scale/offset toward their targets — without it
      // wheel-zoom sets targetScale but nothing visibly moves.
      vp.update()

      const ctx = canvas.getContext('2d')
      if (ctx) {
        const dpr = window.devicePixelRatio || 1
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.scale(dpr, dpr)
        ctx.translate(vp.offset.x, vp.offset.y)
        ctx.scale(vp.scale, vp.scale)

        engine.layout({
          width: canvas.clientWidth,
          height: canvas.clientHeight,
        })
        edgeRenderer.render(ctx, engine.all(), edgesRef.current, vp, edgeStatePool)
      }
      rafId = requestAnimationFrame(render)
    }
    rafId = requestAnimationFrame(render)

    const onResize = () => {
      const stage = stageRef.current
      if (!stage) return
      const dpr = window.devicePixelRatio || 1
      canvas.width = stage.clientWidth * dpr
      canvas.height = stage.clientHeight * dpr
      canvas.style.width = `${stage.clientWidth}px`
      canvas.style.height = `${stage.clientHeight}px`
    }
    onResize()
    window.addEventListener('resize', onResize)

    // Stage→canvas event forwarding. ViewportTransform binds its
    // listeners on the canvas element; cards live in a sibling overlay,
    // so events over cards never reach the canvas naturally. Synthetic
    // re-dispatch keeps pan/zoom alive across the whole stage.
    const stage = stageRef.current
    let detachForwarders: (() => void) | null = null
    if (stage) {
      const forwardWheel = (e: WheelEvent) => {
        if (e.target === canvas) return
        // Wheel over a card scrolls the card text; only modifier-wheel
        // (morph-trigger semantic) forwards to the canvas.
        const target = e.target as Element | null
        if (target?.closest('.canvas-card') && !e.ctrlKey && !e.metaKey) return
        canvas.dispatchEvent(
          new WheelEvent('wheel', {
            deltaX: e.deltaX,
            deltaY: e.deltaY,
            deltaMode: e.deltaMode,
            ctrlKey: e.ctrlKey,
            metaKey: e.metaKey,
            altKey: e.altKey,
            shiftKey: e.shiftKey,
            clientX: e.clientX,
            clientY: e.clientY,
            bubbles: false,
            cancelable: true,
          }),
        )
        if (e.ctrlKey || e.metaKey) e.preventDefault()
      }
      // panActive sustains a pan through fast pointer motion over cards
      // (their pointer-events:auto would otherwise capture the move and
      // freeze the canvas mid-drag).
      let panActive = false
      const forwardMouse = (type: string) => (e: MouseEvent) => {
        if (type === 'mousedown') {
          if (e.target === canvas) {
            panActive = true
            return
          }
          if (
            e.button === 0 &&
            (e.target as Element | null)?.closest('.canvas-card')
          ) {
            return
          }
        } else {
          if (!panActive) return
          if (type === 'mouseup') panActive = false
          if (e.target === canvas) return
        }
        canvas.dispatchEvent(
          new MouseEvent(type, {
            button: e.button,
            buttons: e.buttons,
            clientX: e.clientX,
            clientY: e.clientY,
            bubbles: false,
            cancelable: true,
          }),
        )
        if (type === 'mousedown') panActive = true
      }
      const onDown = forwardMouse('mousedown')
      const onMove = forwardMouse('mousemove')
      const onUp = forwardMouse('mouseup')
      stage.addEventListener('wheel', forwardWheel, { passive: false })
      stage.addEventListener('mousedown', onDown)
      // Document-level move/up: pan must survive the pointer leaving
      // the stage entirely (drag-off-window recovery).
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      detachForwarders = () => {
        stage.removeEventListener('wheel', forwardWheel)
        stage.removeEventListener('mousedown', onDown)
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
    }

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', onResize)
      detachForwarders?.()
      vp.destroy()
    }
  }, [canvasRef, stageRef, engine, edgeRenderer, edgeStatePool])

  const screenToWorld = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const stage = stageRef.current
      if (!stage || !viewport) return { x: clientX, y: clientY }
      const rect = stage.getBoundingClientRect()
      const sx = clientX - rect.left
      const sy = clientY - rect.top
      return {
        x: (sx - viewport.offset.x) / viewport.scale,
        y: (sy - viewport.offset.y) / viewport.scale,
      }
    },
    [stageRef, viewport],
  )

  const zoomToOverview = useCallback(
    (rects: CardRect[]) => {
      if (!viewport) return
      const stage = stageRef.current
      if (!stage || rects.length === 0) return
      const vw = stage.clientWidth
      const vh = stage.clientHeight
      if (vw <= 0 || vh <= 0) return

      let minX = Infinity
      let minY = Infinity
      let maxX = -Infinity
      let maxY = -Infinity
      for (const r of rects) {
        if (r.x < minX) minX = r.x
        if (r.y < minY) minY = r.y
        if (r.x + r.width > maxX) maxX = r.x + r.width
        if (r.y + r.height > maxY) maxY = r.y + r.height
      }
      const PAD = 40
      minX -= PAD
      minY -= PAD
      maxX += PAD
      maxY += PAD
      const bboxW = maxX - minX
      const bboxH = maxY - minY
      const scale = Math.min(vw / bboxW, vh / bboxH) * 0.95
      viewport.setImmediate(
        scale,
        (vw - bboxW * scale) / 2 - minX * scale,
        (vh - bboxH * scale) / 2 - minY * scale,
      )
    },
    [stageRef, viewport],
  )

  const zoomTo100 = useCallback(() => {
    viewport?.setImmediate(1, 0, 0)
  }, [viewport])

  return { viewport, stageSize, screenToWorld, zoomToOverview, zoomTo100 }
}
